import { useMemo, useState } from 'react';
import Filters from 'works/legacy/lib/filters';
import { useHasFilters } from 'works/legacy/lib/hooks/use-has-filters';

export type PaginationDataType = {
  defaultPageSize: number;
  limit: number;
  offset: number;
  orderDirection?: string;
  orderField?: string;
  pageSize?: number;
  current?: number;
};

export type SorterType = {
  columnKey: string;
  field: string;
  order: 'ascend' | 'descend';
};

export type FiltersType = Record<string, string[] | null>;

type Props = {
  localStorageFilterKey: string;
  columnsDictionary: Record<string, string>;
  defaultPaginationData: PaginationDataType;
  filtersOverrideDefault?: FiltersType;
};

export function usePaginationHelper({
  columnsDictionary,
  defaultPaginationData,
  localStorageFilterKey,
  filtersOverrideDefault,
}: Props) {
  const defaultFilters =
    filtersOverrideDefault || Filters.getFilters(localStorageFilterKey) || {};

  const [filters, setFilters] = useState<FiltersType>(defaultFilters);

  const [paginationData, setPaginationData] = useState<PaginationDataType>(
    defaultPaginationData
  );

  const paginationVariables = useMemo(
    () => ({
      ...paginationData,
      filters,
    }),
    [paginationData, filters]
  );

  const tableChangeHandler = (
    pagination: PaginationDataType & { current: number },
    _: FiltersType,
    sorter: SorterType
  ) => {
    const isDescend = sorter.order === 'descend';
    const pageSize =
      pagination.pageSize || defaultPaginationData.defaultPageSize;

    const newPaginationData = {
      ...paginationVariables,
      offset: (pagination.current - 1) * pageSize,
      orderDirection: isDescend ? 'DESC' : 'ASC',
      limit: pageSize,
      orderField:
        (sorter?.columnKey && columnsDictionary[sorter.columnKey]) ||
        defaultPaginationData.orderField,
      current: pagination.current,
    };

    setPaginationData(newPaginationData);
  };

  const filterChangeHandler = (newFilters: FiltersType) => {
    setFilters(newFilters as FiltersType);

    Filters.setFilters(localStorageFilterKey, newFilters);
  };

  // @TODO: consider killing this hook and joining it with this one
  const hasAnyFilter = useHasFilters(filters);

  return {
    filters: filters || {},
    hasAnyFilter,
    paginationVariables,
    localStorageFilterKey,
    filterChangeHandler,
    tableChangeHandler,
  };
}
